import { Input, Label, useDebounceCallback } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useEffect, useMemo, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditionByFieldAndOp,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { useMyDocumentTypesQuery } from "../api/queries";

const ISSUE_DATE_FIELD = "issueDate";

function toYyyyMmDd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function issueDateBoundToInputValue(v: unknown): string {
  if (typeof v !== "string" || !v.trim()) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return toYyyyMmDd(d);
}

function yyyyMmDdStartToFilterIso(yyyyMmDd: string): string {
  return new Date(`${yyyyMmDd}T00:00:00`).toISOString();
}

function yyyyMmDdEndToFilterIso(yyyyMmDd: string): string {
  return new Date(`${yyyyMmDd}T23:59:59.999`).toISOString();
}

export type DocumentTypeTab = "all" | (string & Record<never, never>);
export type DocumentStatusTab =
  | "all"
  | "draft"
  | "issued"
  | "partially_paid"
  | "paid"
  | "cancelled";

type DocumentsFilterProps = {
  searchParams: OffsetPageQueryDTO & { documentType?: string };
  fromRoute: string;
};

export function DocumentsFilter({ searchParams, fromRoute }: DocumentsFilterProps) {
  const nav = useNavigate({ from: fromRoute as "/app/documents" | "/app/my-documents" });
  const search = searchParams;
  const filters = search.filters ?? [];
  const documentTypeFilter = findCondition(filters, "documentType");
  const documentTypeTab: string = (documentTypeFilter?.value as string) ?? "all";

  const { data: documentTypesData } = useMyDocumentTypesQuery({
    limit: 100,
  });
  const documentTypeOptions = useMemo(() => {
    const list = documentTypesData?.items ?? [];
    const fromDb = [...list]
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((t) => ({ value: t.code, label: t.name }));
    return [{ value: "all", label: "ທັງໝົດ" }, ...fromDb];
  }, [documentTypesData?.items]);
  const statusFilter = findCondition(filters, "status");
  const statusTab: DocumentStatusTab =
    (statusFilter?.value as DocumentStatusTab) ?? "all";
  const searchFilter = findCondition(filters, "search");
  const [searchValue, setSearchValue] = useState<string>(
    (searchFilter?.value as string) ?? "",
  );

  useEffect(() => {
    setSearchValue((searchFilter?.value as string) ?? "");
  }, [searchFilter?.value]);

  const setTab = (tab: string) => {
    let nextFilters: FilterConditionDTO[] = filters;
    if (tab === "all") {
      nextFilters = removeConditions(filters, "documentType") ?? [];
    } else {
      nextFilters = upsertCondition(filters, {
        field: "documentType",
        op: "eq",
        value: tab,
      });
    }
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        filters: nextFilters.length ? nextFilters : undefined,
      },
    });
  };

  const setStatusTab = (tab: DocumentStatusTab) => {
    let nextFilters: FilterConditionDTO[] = filters;
    if (tab === "all") {
      nextFilters = removeConditions(filters, "status") ?? [];
    } else {
      nextFilters = upsertCondition(filters, {
        field: "status",
        op: "eq",
        value: tab,
      });
    }
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        filters: nextFilters.length ? nextFilters : undefined,
      },
    });
  };

  const debouncedSearch = useDebounceCallback((val: string) => {
    let nextFilters: FilterConditionDTO[] = filters;
    if (val.trim()) {
      nextFilters = upsertCondition(filters, {
        field: "search",
        op: "contains",
        value: val.trim(),
      });
    } else {
      nextFilters = removeConditions(filters, "search") ?? [];
    }
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        filters: nextFilters.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const issueDateGte = filters.find(
    (f) => f.field === ISSUE_DATE_FIELD && f.op === "gte",
  );
  const issueDateLte = filters.find(
    (f) => f.field === ISSUE_DATE_FIELD && f.op === "lte",
  );
  const issueDateFromValue = issueDateBoundToInputValue(issueDateGte?.value);
  const issueDateToValue = issueDateBoundToInputValue(issueDateLte?.value);

  const navigateWithFilters = (nextFilters: FilterConditionDTO[]) => {
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        filters: nextFilters.length ? nextFilters : undefined,
      },
    });
  };

  const setIssueDateFrom = (yyyyMmDd: string) => {
    let base =
      removeConditionByFieldAndOp(filters, ISSUE_DATE_FIELD, "gte") ?? [];
    if (yyyyMmDd.trim()) {
      base = [
        ...base,
        {
          field: ISSUE_DATE_FIELD,
          op: "gte",
          value: yyyyMmDdStartToFilterIso(yyyyMmDd.trim()),
        },
      ];
    }
    navigateWithFilters(base);
  };

  const setIssueDateTo = (yyyyMmDd: string) => {
    let base =
      removeConditionByFieldAndOp(filters, ISSUE_DATE_FIELD, "lte") ?? [];
    if (yyyyMmDd.trim()) {
      base = [
        ...base,
        {
          field: ISSUE_DATE_FIELD,
          op: "lte",
          value: yyyyMmDdEndToFilterIso(yyyyMmDd.trim()),
        },
      ];
    }
    navigateWithFilters(base);
  };

  const clearIssueDateRange = () => {
    let base = removeConditionByFieldAndOp(filters, ISSUE_DATE_FIELD, "gte") ?? [];
    base = removeConditionByFieldAndOp(base, ISSUE_DATE_FIELD, "lte") ?? [];
    navigateWithFilters(base);
  };

  const statusTabs: { key: DocumentStatusTab; label: string }[] = [
    { key: "all", label: "ທັງໝົດ" },
    { key: "draft", label: "ຮ່າງ" },
    { key: "issued", label: "ອອກແລ້ວ" },
    { key: "partially_paid", label: "ຊຳລະບາງສ່ວນ" },
    { key: "paid", label: "ຊຳລະແລ້ວ" },
    { key: "cancelled", label: "ຍົກເລີກ" },
  ];

  return (
    <div className="flex flex-col gap-4" data-tourid="documents-filter">
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="ຄົ້ນຫາເອກະສານ..."
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            debouncedSearch(e.target.value);
          }}
          className="pl-9"
        />
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Label className="shrink-0 text-muted-foreground text-sm">ປະເພດ:</Label>
          <SimpleSelect
            value={documentTypeTab}
            onValueChange={setTab}
            placeholder="ທັງໝົດ"
            className="h-9 min-w-[140px]"
            options={documentTypeOptions}
          />
        </div>
        <div className="flex items-center gap-2">
          <Label className="shrink-0 text-muted-foreground text-sm">ສະຖານະ:</Label>
          <SimpleSelect
            value={statusTab}
            onValueChange={(val) => setStatusTab(val as DocumentStatusTab)}
            placeholder="ທັງໝົດ"
            className="h-9 min-w-[140px]"
            options={statusTabs.map((tab) => ({ value: tab.key, label: tab.label }))}
          />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex min-w-40 flex-col gap-1">
            <Label
              htmlFor="qi-my-filter-issue-from"
              className="text-muted-foreground text-xs"
            >
              ວັນທີອອກ (ຕັ້ງແຕ່)
            </Label>
            <Input
              id="qi-my-filter-issue-from"
              type="date"
              value={issueDateFromValue}
              onChange={(e) => setIssueDateFrom(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="flex min-w-40 flex-col gap-1">
            <Label
              htmlFor="qi-my-filter-issue-to"
              className="text-muted-foreground text-xs"
            >
              ຮອດ
            </Label>
            <Input
              id="qi-my-filter-issue-to"
              type="date"
              value={issueDateToValue}
              onChange={(e) => setIssueDateTo(e.target.value)}
              className="h-9"
            />
          </div>
          {(issueDateFromValue || issueDateToValue) && (
            <button
              type="button"
              onClick={clearIssueDateRange}
              className="mb-0.5 text-muted-foreground text-xs underline-offset-2 hover:text-foreground hover:underline"
            >
              ລ້າງຊ່ວງວັນທີ
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
